import React, { useState, useEffect } from 'react';
import {
  ServiceRequest,
  categoryLabels,
  priorityLabels,
  statusLabels,
} from '@/types/shared/serviceRequest';
import { UserRole } from '@/types/shared/auth';
import { useEntityContext } from '@/hooks/entities/vendor';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PaymentModal } from '@/components/cross-entity/payments/PaymentModal';
import { ServiceRequestConversations } from './ServiceRequestConversations';
import {
  Clock,
  MapPin,
  DollarSign,
  User,
  Calendar,
  FileText,
  Tag,
  MessageSquare,
  Star,
  Building,
  CheckCircle,
  AlertCircle,
  Phone,
  Mail,
  Camera,
  Edit,
  Trash,
  AlertTriangle,
  Info,
  Send,
} from 'lucide-react';

// Type definitions for better type safety
interface VendorBid {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorRating?: number;
  reviewCount?: number;
  vendorAvatar?: string;
  estimatedCost?: number;
  estimatedDuration?: string;
  availability?: string;
  warranty?: string;
  experience?: string;
  message?: string;
  notes?: string;
  certifications?: string[];
  createdAt?: string;
  vendorPhone?: string;
  vendorEmail?: string;
}

interface ConversationMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'resident' | 'vendor';
  content: string;
  timestamp: string;
  isRead: boolean;
}

interface Conversation {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorAvatar?: string;
  vendorRating?: number;
  vendorPhone?: string;
  vendorEmail?: string;
  messages: ConversationMessage[];
  isActive: boolean;
  lastActivity: string;
}

interface ServiceRequestDetailModalProps {
  serviceRequest: ServiceRequest;
  userRole: UserRole;
  userId?: string;
  isOpen: boolean;
  onClose: () => void;
  onRespond?: () => void;
}

export function ServiceRequestDetailModal({
  serviceRequest,
  userRole,
  userId,
  isOpen,
  onClose,
  onRespond,
}: ServiceRequestDetailModalProps) {
  const { entityType, capabilities } = useEntityContext();
  const [activeTab, setActiveTab] = useState('details');
  const [newMessage, setNewMessage] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState<VendorBid | null>(null);

  // Conversation management state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string>('');

  // Initialize mock conversations for demonstration
  useEffect(() => {
    if (serviceRequest.status !== 'open') {
      // If service is assigned, show conversation with assigned vendor
      const assignedConversation: Conversation = {
        id: `conv-${serviceRequest.id}-assigned`,
        vendorId: 'assigned-vendor',
        vendorName: mockVendorInfo.name,
        vendorAvatar: mockVendorInfo.avatar,
        vendorRating: mockVendorInfo.rating,
        vendorPhone: mockVendorInfo.phone,
        vendorEmail: mockVendorInfo.email,
        messages: mockMessages.map((msg) => ({
          id: msg.id,
          senderId: msg.sender === 'vendor' ? 'assigned-vendor' : userId || 'current-user',
          senderName: msg.senderName,
          senderType: msg.sender as 'resident' | 'vendor',
          content: msg.message,
          timestamp: msg.timestamp,
          isRead: msg.isRead,
        })),
        isActive: true,
        lastActivity: mockMessages[mockMessages.length - 1]?.timestamp || new Date().toISOString(),
      };
      setConversations([assignedConversation]);
      setActiveConversationId(assignedConversation.id);
    }
  }, [serviceRequest.status, serviceRequest.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const canRespond =
    userRole === 'vendor' &&
    serviceRequest.status === 'open' &&
    !serviceRequest.responses?.some((r) => r.vendorId === userId) &&
    capabilities?.canSubmitProposals;

  const canEdit = userRole === 'resident' && serviceRequest.status === 'open';

  const handleAcceptBid = (bid: VendorBid) => {
    setSelectedBid(bid);
    setShowPaymentModal(true);
  };

  const handleMessageVendor = (bid?: VendorBid) => {
    const vendorId = bid?.vendorId || bid?.id || 'assigned-vendor';
    const vendorName = bid?.vendorName || mockVendorInfo.name;

    // Check if conversation already exists
    const existingConversation = conversations.find((c) => c.vendorId === vendorId);

    if (existingConversation) {
      // Switch to Messages tab and highlight the conversation
      setActiveTab('communication');
      setActiveConversationId(existingConversation.id);
    } else {
      // Start new conversation
      const conversationId = startNewConversation(vendorId, vendorName, bid);
      setActiveTab('communication');
      setActiveConversationId(conversationId);
    }
  };

  const handleMessageResident = () => {
    // For vendor messaging resident - similar logic
    const residentId = serviceRequest.requestedBy?.id || 'current-resident';
    const residentName = serviceRequest.requestedBy?.name || 'Resident';

    const existingConversation = conversations.find((c) => c.vendorId === residentId);

    if (existingConversation) {
      setActiveTab('communication');
      setActiveConversationId(existingConversation.id);
    } else {
      const conversationId = startNewConversation(residentId, residentName);
      setActiveTab('communication');
      setActiveConversationId(conversationId);
    }
  };

  const startNewConversation = (vendorId: string, vendorName: string, bidData?: VendorBid) => {
    const conversationId = `conv-${serviceRequest.id}-${vendorId}-${Date.now()}`;

    const newConversation: Conversation = {
      id: conversationId,
      vendorId,
      vendorName,
      vendorAvatar: bidData?.vendorAvatar || '/placeholder.svg',
      vendorRating: bidData?.vendorRating || mockVendorInfo.rating,
      vendorPhone: bidData?.vendorPhone || mockVendorInfo.phone,
      vendorEmail: bidData?.vendorEmail || mockVendorInfo.email,
      messages: [],
      isActive: true,
      lastActivity: new Date().toISOString(),
    };

    setConversations((prev) => [...prev, newConversation]);
    return conversationId;
  };

  const handleSendMessage = (conversationId: string, message: string) => {
    const newMessage: ConversationMessage = {
      id: `msg-${Date.now()}`,
      senderId: userId || 'current-user',
      senderName: userRole === 'resident' ? 'You' : 'Vendor',
      senderType: userRole as 'resident' | 'vendor',
      content: message,
      timestamp: new Date().toISOString(),
      isRead: true,
    };

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              messages: [...conv.messages, newMessage],
              lastActivity: new Date().toISOString(),
            }
          : conv,
      ),
    );

    // In a real app, this would send the message via API
    console.log('Message sent:', { conversationId, message });
  };

  const handleConversationCreate = (conversation: Conversation) => {
    console.log('Conversation create handled internally');
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'open':
        return 'outline';
      case 'in_review':
        return 'secondary';
      case 'assigned':
        return 'default';
      case 'in_progress':
        return 'default';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-4 h-4" />;
      case 'in_review':
        return <Clock className="w-4 h-4" />;
      case 'assigned':
        return <User className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'cancelled':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getStatusProgress = (status: string) => {
    switch (status) {
      case 'open':
        return 20;
      case 'in_review':
        return 40;
      case 'assigned':
        return 60;
      case 'in_progress':
        return 80;
      case 'completed':
        return 100;
      case 'cancelled':
        return 0;
      default:
        return 0;
    }
  };

  const mockVendorInfo = {
    name: 'ProFix Appliance Services',
    rating: 4.8,
    avatar: '/placeholder.svg',
    phone: '(555) 123-4567',
    email: 'contact@profix.com',
    estimatedArrival: 'Today, 2:30 PM',
    workProgress: 75,
  };

  const mockTimeline = [
    {
      date: '2025-08-18 08:00 AM',
      title: 'Request Submitted',
      description: 'Service request created and posted for vendor bidding',
      status: 'completed',
    },
    {
      date: '2025-08-18 10:30 AM',
      title: 'Vendor Assigned',
      description: 'ProFix Appliance Services selected and notified',
      status: 'completed',
    },
    {
      date: '2025-08-18 02:30 PM',
      title: 'Service Scheduled',
      description: 'Technician estimated arrival time confirmed',
      status: 'current',
    },
    {
      date: 'Expected by 5:00 PM',
      title: 'Work Completion',
      description: 'Service completion and quality verification',
      status: 'pending',
    },
  ];

  // Generate realistic vendor bids based on service category
  const generateMockBids = (category: string) => {
    const baseBids: Record<string, VendorBid[]> = {
      plumbing: [
        {
          id: 'bid-plumb-1',
          vendorId: 'vendor-plumb-1',
          vendorName: 'AquaFix Plumbing Solutions',
          vendorRating: 4.8,
          reviewCount: 127,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 185,
          estimatedDuration: '2-3 hours',
          availability: 'Available today, 2:00 PM',
          warranty: '1-year parts & labor',
          experience: '15+ years residential plumbing',
          message:
            'I can fix your leak issue today. I have all necessary parts in my truck and offer a full warranty on all work. Licensed and insured.',
          certifications: ['Licensed Plumber', 'Bonded & Insured', 'Emergency Services'],
          createdAt: '2025-08-18T09:15:00Z',
        },
        {
          id: 'bid-plumb-2',
          vendorId: 'vendor-plumb-2',
          vendorName: 'DrainMaster Pro Services',
          vendorRating: 4.6,
          reviewCount: 89,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 165,
          estimatedDuration: '1-2 hours',
          availability: 'Tomorrow morning, 9:00 AM',
          warranty: '90-day labor warranty',
          experience: '10+ years drain specialist',
          message:
            'Specialized in drain and pipe repairs. Can provide same-day service with competitive pricing.',
          certifications: ['State Certified', 'Drain Specialist'],
          createdAt: '2025-08-18T09:45:00Z',
        },
      ],
      electrical: [
        {
          id: 'bid-elec-1',
          vendorId: 'vendor-elec-1',
          vendorName: 'BrightSpark Electrical',
          vendorRating: 4.9,
          reviewCount: 203,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 225,
          estimatedDuration: '3-4 hours',
          availability: 'Today, 3:00 PM',
          warranty: '2-year warranty',
          experience: '20+ years electrical work',
          message:
            'Licensed master electrician. I can resolve your electrical issue safely and up to code. All work guaranteed.',
          certifications: ['Master Electrician', 'Code Certified', 'Safety Trained'],
          createdAt: '2025-08-18T08:30:00Z',
        },
        {
          id: 'bid-elec-2',
          vendorId: 'vendor-elec-2',
          vendorName: 'PowerLine Electric Co.',
          vendorRating: 4.7,
          reviewCount: 156,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 195,
          estimatedDuration: '2-3 hours',
          availability: 'Tomorrow, 1:00 PM',
          warranty: '1-year parts warranty',
          experience: '12+ years residential electrical',
          message:
            'Competitive rates with quality service. Available for emergency calls and provide detailed estimates.',
          certifications: ['Licensed Electrician', 'Insured'],
          createdAt: '2025-08-18T10:00:00Z',
        },
      ],
      hvac: [
        {
          id: 'bid-hvac-1',
          vendorId: 'vendor-hvac-1',
          vendorName: 'CoolTech HVAC Services',
          vendorRating: 4.8,
          reviewCount: 145,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 175,
          estimatedDuration: '2-3 hours',
          availability: 'Today, 4:00 PM',
          warranty: '1-year service warranty',
          experience: '18+ years HVAC specialist',
          message:
            'EPA certified technician. I can diagnose and repair your HVAC system efficiently. Free estimates on major repairs.',
          certifications: ['EPA Certified', 'HVAC Licensed', 'Refrigerant Certified'],
          createdAt: '2025-08-18T09:00:00Z',
        },
      ],
      landscaping: [
        {
          id: 'bid-land-1',
          vendorId: 'vendor-land-1',
          vendorName: 'Green Thumb Landscaping',
          vendorRating: 4.7,
          reviewCount: 98,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 145,
          estimatedDuration: '4-5 hours',
          availability: 'This weekend',
          warranty: '30-day plant guarantee',
          experience: '8+ years landscaping',
          message:
            'Professional landscaping with eco-friendly practices. All plants come with a guarantee.',
          certifications: ['Certified Arborist', 'Pesticide Licensed'],
          createdAt: '2025-08-18T08:45:00Z',
        },
      ],
      general_maintenance: [
        {
          id: 'bid-maint-1',
          vendorId: 'vendor-maint-1',
          vendorName: 'HandyFix Solutions',
          vendorRating: 4.6,
          reviewCount: 112,
          vendorAvatar: '/placeholder.svg',
          estimatedCost: 125,
          estimatedDuration: '2-4 hours',
          availability: 'Flexible scheduling',
          warranty: '90-day workmanship',
          experience: '12+ years handyman services',
          message:
            'Reliable handyman service for all your maintenance needs. Licensed and insured.',
          certifications: ['Licensed Contractor', 'Insured'],
          createdAt: '2025-08-18T09:30:00Z',
        },
      ],
    };

    return baseBids[category] || baseBids['general_maintenance'];
  };

  const mockBids = serviceRequest.responses || generateMockBids(serviceRequest.category);

  const mockMessages = [
    {
      id: '1',
      sender: 'resident',
      senderName: 'John Smith',
      avatar: '/placeholder.svg',
      message: 'Hi! Just wanted to confirm the service time for tomorrow. Is 2:30 PM still good?',
      timestamp: '2025-08-18 1:45 PM',
      isRead: true,
    },
    {
      id: '2',
      sender: 'vendor',
      senderName: 'ProFix Services',
      avatar: '/placeholder.svg',
      message:
        "Yes, 2:30 PM tomorrow works perfectly. I'll bring all necessary tools and the replacement part we discussed.",
      timestamp: '2025-08-18 1:52 PM',
      isRead: true,
    },
    {
      id: '3',
      sender: 'resident',
      senderName: 'John Smith',
      avatar: '/placeholder.svg',
      message:
        "Perfect! I'll make sure someone is home to let you in. Do you need access to the electrical panel?",
      timestamp: '2025-08-18 2:00 PM',
      isRead: true,
    },
    {
      id: '4',
      sender: 'vendor',
      senderName: 'ProFix Services',
      avatar: '/placeholder.svg',
      message:
        'Yes, access to the electrical panel would be helpful. Also, just a heads up - I might arrive 15 minutes early to set up.',
      timestamp: '2025-08-18 2:15 PM',
      isRead: false,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden">
        <DialogHeader className="space-y-4">
          <div className="flex items-start justify-between pr-8">
            <div className="space-y-2">
              <DialogTitle className="text-2xl">{serviceRequest.title}</DialogTitle>
              <DialogDescription className="text-base">
                Request #{serviceRequest.id} • {categoryLabels[serviceRequest.category]}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={getPriorityBadgeVariant(serviceRequest.priority)} className="text-xs">
                {priorityLabels[serviceRequest.priority]} Priority
              </Badge>
              <Badge
                variant={getStatusBadgeVariant(serviceRequest.status)}
                className="text-xs flex items-center gap-1"
              >
                {getStatusIcon(serviceRequest.status)}
                {statusLabels[serviceRequest.status]}
              </Badge>
            </div>
          </div>

          {/* Status Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">
                {getStatusProgress(serviceRequest.status)}% Complete
              </span>
            </div>
            <Progress value={getStatusProgress(serviceRequest.status)} className="h-2" />
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="vendor">
              {serviceRequest.status === 'open' ? 'Bids' : 'Vendor'}
              {(serviceRequest.responses && serviceRequest.responses.length > 0) ||
              mockBids.length > 0 ? (
                <Badge variant="secondary" className="ml-2">
                  {serviceRequest.responses?.length || mockBids.length}
                </Badge>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="communication">Messages</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-hidden mt-4">
            <ScrollArea className="h-[calc(90vh-280px)]">
              <div className="space-y-6 pr-4">
                <TabsContent value="details" className="mt-0 space-y-6">
                  {/* Main Request Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Request Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                          DESCRIPTION
                        </h4>
                        <p className="leading-relaxed">{serviceRequest.description}</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                            CATEGORY
                          </h4>
                          <Badge variant="outline" className="text-sm">
                            <Tag className="w-3 h-3 mr-1" />
                            {categoryLabels[serviceRequest.category]}
                          </Badge>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                            PRIORITY
                          </h4>
                          <Badge
                            variant={getPriorityBadgeVariant(serviceRequest.priority)}
                            className="text-sm"
                          >
                            {priorityLabels[serviceRequest.priority]}
                          </Badge>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                            CREATED
                          </h4>
                          <p className="text-sm">
                            {new Date(serviceRequest.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Location Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Location & Access
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                            ADDRESS
                          </h4>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4 text-muted-foreground" />
                              <span>{serviceRequest.location?.building}</span>
                            </div>
                            {serviceRequest.location?.unit && (
                              <p className="text-sm text-muted-foreground ml-6">
                                Unit {serviceRequest.location.unit}
                              </p>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2 text-sm text-muted-foreground">
                            REQUESTED BY
                          </h4>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src="/placeholder.svg" />
                              <AvatarFallback>
                                {serviceRequest.requestedBy?.name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{serviceRequest.requestedBy?.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {serviceRequest.requestedBy?.unitNumber &&
                                  `Unit ${serviceRequest.requestedBy.unitNumber}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Budget & Timeline */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {serviceRequest.budget && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5" />
                            Budget
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="text-2xl font-bold text-green-600">
                              {serviceRequest.budget.min && `$${serviceRequest.budget.min}`}
                              {serviceRequest.budget.min && serviceRequest.budget.max && ' - '}
                              {serviceRequest.budget.max && `$${serviceRequest.budget.max}`}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {serviceRequest.budget.currency || 'USD'}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {serviceRequest.timeline && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Timeline
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {serviceRequest.timeline.preferredStartDate && (
                            <div>
                              <h4 className="font-medium text-sm text-muted-foreground">
                                PREFERRED START
                              </h4>
                              <p className="text-sm">
                                {new Date(
                                  serviceRequest.timeline.preferredStartDate,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          {serviceRequest.timeline.deadline && (
                            <div>
                              <h4 className="font-medium text-sm text-muted-foreground">
                                DEADLINE
                              </h4>
                              <p className="text-sm font-medium">
                                {new Date(serviceRequest.timeline.deadline).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Photos */}
                  {serviceRequest.attachments && serviceRequest.attachments.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Camera className="w-5 h-5" />
                          Photos ({serviceRequest.attachments.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {serviceRequest.attachments.map((attachment, index) => (
                            <div key={attachment.id} className="relative group">
                              <img
                                src={attachment.url || '/placeholder.svg'}
                                alt={`Attachment ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  View
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="timeline" className="mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle>Service Timeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {mockTimeline.map((item, index) => (
                          <div key={index} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                                  item.status === 'completed'
                                    ? 'bg-green-100 border-green-500'
                                    : item.status === 'current'
                                      ? 'bg-blue-100 border-blue-500'
                                      : 'bg-gray-100 border-gray-300'
                                }`}
                              >
                                {item.status === 'completed' ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : item.status === 'current' ? (
                                  <Clock className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                                )}
                              </div>
                              {index < mockTimeline.length - 1 && (
                                <div className="w-0.5 h-8 bg-gray-200 mt-2" />
                              )}
                            </div>
                            <div className="flex-1 pb-4">
                              <div className="flex justify-between items-start mb-1">
                                <h4 className="font-medium">{item.title}</h4>
                                <span className="text-sm text-muted-foreground">{item.date}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="vendor" className="mt-0">
                  {serviceRequest.status === 'open' ? (
                    // Show vendor bids for open requests
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span>Vendor Bids</span>
                          {((serviceRequest.responses && serviceRequest.responses.length > 0) ||
                            mockBids.length > 0) && (
                            <Badge variant="secondary">
                              {serviceRequest.responses?.length || mockBids.length} bid
                              {(serviceRequest.responses?.length || mockBids.length) > 1
                                ? 's'
                                : ''}{' '}
                              received
                            </Badge>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {!serviceRequest.responses || serviceRequest.responses.length === 0 ? (
                          <div className="text-center py-8">
                            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-medium mb-2">Waiting for Vendor Bids</h3>
                            <p className="text-sm text-muted-foreground">
                              Vendors will start submitting bids soon. You'll be notified when bids
                              are received.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {mockBids.map((bid: VendorBid, index: number) => (
                              <div
                                key={bid.id || index}
                                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start gap-3">
                                    <Avatar className="w-12 h-12">
                                      <AvatarImage src={bid.vendorAvatar || '/placeholder.svg'} />
                                      <AvatarFallback>
                                        {bid.vendorName?.charAt(0) || 'V'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <h3 className="font-semibold">
                                        {bid.vendorName || 'Anonymous Vendor'}
                                      </h3>
                                      {bid.vendorRating && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                          <span className="text-sm">{bid.vendorRating}</span>
                                          <span className="text-xs text-muted-foreground">
                                            ({bid.reviewCount || 0} reviews)
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <div className="text-lg font-bold text-green-600">
                                      {bid.estimatedCost ? `$${bid.estimatedCost}` : 'Quote TBD'}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {bid.estimatedDuration || 'Timeline TBD'}
                                    </div>
                                  </div>
                                </div>

                                <p className="text-sm mb-3">
                                  {bid.message || bid.notes || 'No additional details provided.'}
                                </p>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-3">
                                  {bid.availability && (
                                    <div>
                                      <span className="font-medium text-muted-foreground">
                                        Available:{' '}
                                      </span>
                                      <span>{bid.availability}</span>
                                    </div>
                                  )}
                                  {bid.warranty && (
                                    <div>
                                      <span className="font-medium text-muted-foreground">
                                        Warranty:{' '}
                                      </span>
                                      <span>{bid.warranty}</span>
                                    </div>
                                  )}
                                  {bid.experience && (
                                    <div>
                                      <span className="font-medium text-muted-foreground">
                                        Experience:{' '}
                                      </span>
                                      <span>{bid.experience}</span>
                                    </div>
                                  )}
                                </div>

                                {bid.certifications && bid.certifications.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-sm font-medium mb-2 text-muted-foreground">
                                      Certifications:
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {bid.certifications.map((cert: string, i: number) => (
                                        <Badge key={i} variant="secondary" className="text-xs">
                                          {cert}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="flex items-center justify-between pt-3 border-t">
                                  <div className="text-xs text-muted-foreground">
                                    Submitted{' '}
                                    {new Date(bid.createdAt || new Date()).toLocaleDateString()}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleMessageVendor(bid)}
                                    >
                                      <MessageSquare className="w-4 h-4 mr-1" />
                                      Message
                                    </Button>
                                    {userRole === 'resident' && (
                                      <Button size="sm" onClick={() => handleAcceptBid(bid)}>
                                        View Cost
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    // Show assigned vendor for non-open requests
                    <Card>
                      <CardHeader>
                        <CardTitle>Assigned Vendor</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-16 h-16">
                            <AvatarImage src={mockVendorInfo.avatar} />
                            <AvatarFallback>{mockVendorInfo.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{mockVendorInfo.name}</h3>
                              <div className="flex items-center">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                <span className="text-sm ml-1">{mockVendorInfo.rating}</span>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-4 h-4" />
                                <span>{mockVendorInfo.phone}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Mail className="w-4 h-4" />
                                <span>{mockVendorInfo.email}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {serviceRequest.status === 'in_progress' && (
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="font-medium">Work Progress</span>
                              <span className="text-sm text-muted-foreground">
                                {mockVendorInfo.workProgress}%
                              </span>
                            </div>
                            <Progress value={mockVendorInfo.workProgress} className="h-2" />
                            <p className="text-sm text-muted-foreground">
                              Estimated arrival: {mockVendorInfo.estimatedArrival}
                            </p>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button variant="outline" size="sm">
                            <Phone className="w-4 h-4 mr-2" />
                            Call Vendor
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleMessageVendor()}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Send Message
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="communication" className="mt-0">
                  <Card>
                    <CardHeader>
                      <CardTitle>Communication History</CardTitle>
                      <CardDescription>
                        Messages with vendors for this service request
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ServiceRequestConversations
                        serviceRequestId={serviceRequest.id}
                        userRole={userRole as 'resident' | 'vendor'}
                        userId={userId || 'current-user'}
                        conversations={conversations}
                        onSendMessage={handleSendMessage}
                        activeConversationId={activeConversationId}
                        onConversationChange={setActiveConversationId}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
              </div>
            </ScrollArea>
          </div>
        </Tabs>

        <Separator />

        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            {canEdit && (
              <>
                <Button variant="outline" size="sm">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Request
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                  <Trash className="w-4 h-4 mr-2" />
                  Cancel Request
                </Button>
              </>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {canRespond && onRespond && <Button onClick={onRespond}>Submit Response</Button>}
          </div>
        </div>
      </DialogContent>

      {/* Payment Modal */}
      {selectedBid && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          serviceRequest={{
            id: serviceRequest.id,
            title: serviceRequest.title,
            vendor: {
              name: selectedBid?.vendorName || 'Selected Vendor',
              rating: selectedBid?.vendorRating || 4.5,
            },
            totalAmount: selectedBid?.estimatedCost || 0,
            groupTokensAvailable: 0, // Default to 0 if not available
            isGroupBuy: false, // Default to false if not available
          }}
        />
      )}
    </Dialog>
  );
}
